import React, { useState, useEffect, useCallback } from "react";
import {
  getJalons, createJalon, updateJalon, proposerJalon, confirmerJalon, refuserJalon, deleteJalon,
} from "../services/api";
import { ModaleJalon, ModaleSimple, afficherNombre } from "./ProjetJalonsModalesV2";
import {
  LIBELLES_STATUT_JALON, classeStatutJalon, formatDate, formatHorizon, largeurJauge,
} from "../services/projet";

// ===================================================================
// The Crowd v2 — Fiche projet : jalons
//
// Un jalon est un objectif valorise, pas une echeance.
//   en_cours --proposer (acteur principal)--> propose
//   propose  --confirmer (chef, co-chefs)---> atteint
//   propose  --ne pas confirmer, avec un mot-> en_cours
// Creation, modification, suppression : chef et co-chefs. L'acteur
// principal peut mettre a jour la valeur actuelle de la cible.
//
// Props : projetId, collaborateurId, droits
// Modales : ProjetJalonsModalesV2.js
// ===================================================================

// --- Carte d'un jalon ---

function CarteJalon({ jalon, droits, onAction }) {
  const gerer = Boolean(droits?.peut_gerer);
  const principal = Boolean(droits?.peut_proposer_jalon);
  const aCible = jalon.cible_visee !== null && jalon.cible_visee !== undefined;
  const horizon = formatHorizon(jalon.horizon);

  return (
    <article className={`v2-jalon v2-jalon-${jalon.statut}`}>
      <header className="v2-jalon-entete">
        <h3 className="v2-jalon-titre">{jalon.intitule}</h3>
        <span className={classeStatutJalon(jalon.statut)}>
          {jalon.statut === "atteint"
            ? `Atteint le ${formatDate(jalon.atteint_le)}`
            : LIBELLES_STATUT_JALON[jalon.statut]}
        </span>
      </header>

      <p className="v2-jalon-critere">{jalon.critere}</p>

      {aCible && (
        <div className="v2-projet-jauge">
          <span className="v2-barres-piste">
            <span className="v2-barres-valeur v2-ton-fort" style={{ width: largeurJauge(jalon.cible_actuelle || 0, jalon.cible_visee) }} />
          </span>
          <span className="v2-projet-jauge-valeur">
            {afficherNombre(jalon.cible_actuelle)} / {afficherNombre(jalon.cible_visee)}{jalon.cible_unite ? ` ${jalon.cible_unite}` : ""}
          </span>
        </div>
      )}

      <div className="v2-jalon-meta">
        {horizon && <span>Horizon : {horizon}</span>}
        {jalon.etapes_total > 0 && <span>{jalon.etapes_terminees} étape{jalon.etapes_terminees > 1 ? "s" : ""} sur {jalon.etapes_total} terminée{jalon.etapes_total > 1 ? "s" : ""}</span>}
        {jalon.statut === "propose" && jalon.propose_par_nom && (
          <span>Proposé le {formatDate(jalon.propose_le)} par {jalon.propose_par_nom}</span>
        )}
        {jalon.statut === "atteint" && jalon.confirme_par_nom && <span>Confirmé par {jalon.confirme_par_nom}</span>}
      </div>

      {jalon.statut === "en_cours" && jalon.commentaire && (
        <p className="v2-jalon-mot">Pas encore confirmé : « {jalon.commentaire} »</p>
      )}

      {(gerer || principal) && jalon.statut !== "atteint" && (
        <div className="v2-jalon-actions">
          {principal && jalon.statut === "en_cours" && (
            <button type="button" className="v2-btn v2-btn-principal v2-btn-compact" onClick={() => onAction("proposer", jalon)}>
              Proposer comme atteint
            </button>
          )}
          {gerer && jalon.statut === "propose" && (
            <>
              <button type="button" className="v2-btn v2-btn-principal v2-btn-compact" onClick={() => onAction("confirmer", jalon)}>
                Confirmer
              </button>
              <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => onAction("refuser", jalon)}>
                Ne pas confirmer
              </button>
            </>
          )}
          {(principal || gerer) && aCible && (
            <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => onAction("valeur", jalon)}>
              Mettre à jour la valeur
            </button>
          )}
          {gerer && (
            <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => onAction("modifier", jalon)}>
              Modifier
            </button>
          )}
        </div>
      )}
      {gerer && (
        <button type="button" className="v2-lien-ligne v2-jalon-supprimer" onClick={() => onAction("supprimer", jalon)}>
          Supprimer le jalon
        </button>
      )}
    </article>
  );
}

// --- Bloc ---

function ProjetJalonsV2({ projetId, collaborateurId, droits }) {
  const [jalons, setJalons] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [modale, setModale] = useState(null); // { type, jalon }

  const charger = useCallback(() => {
    setErreur(null);
    return getJalons(projetId, collaborateurId)
      .then(setJalons)
      .catch((err) => setErreur(`Jalons non chargés : ${err.message}`));
  }, [projetId, collaborateurId]);

  useEffect(() => {
    charger();
  }, [charger]);

  const fermerEtRecharger = async () => {
    setModale(null);
    await charger();
  };

  const onAction = (type, jalon) => setModale({ type, jalon });

  const renderModale = () => {
    if (!modale) return null;
    const { type, jalon } = modale;
    const fermer = () => setModale(null);

    if (type === "creer" || type === "modifier") {
      return (
        <ModaleJalon
          jalon={type === "modifier" ? jalon : null}
          projetId={projetId}
          collaborateurId={collaborateurId}
          onClose={fermer}
          onEnregistrer={async (valeurs) => {
            if (type === "modifier") await updateJalon(jalon.id, valeurs, collaborateurId);
            else await createJalon(projetId, valeurs, collaborateurId);
            await fermerEtRecharger();
          }}
        />
      );
    }
    if (type === "proposer") {
      return (
        <ModaleSimple titre="Proposer le jalon comme atteint" libelleAction="Proposer" onClose={fermer}
          aide={`« ${jalon.intitule} » — le chef de projet sera invité à le confirmer.`}
          onValider={async () => { await proposerJalon(jalon.id, collaborateurId); await fermerEtRecharger(); }} />
      );
    }
    if (type === "confirmer") {
      return (
        <ModaleSimple titre="Confirmer le jalon" libelleAction="Confirmer" onClose={fermer}
          aide={`« ${jalon.intitule} » passera au statut atteint, daté d'aujourd'hui.`}
          onValider={async () => { await confirmerJalon(jalon.id, collaborateurId); await fermerEtRecharger(); }} />
      );
    }
    if (type === "refuser") {
      return (
        <ModaleSimple titre="Ne pas confirmer le jalon" libelleAction="Envoyer" onClose={fermer}
          aide="Le jalon repasse en cours. Un mot d'explication est demandé pour l'acteur principal."
          champ={{ libelle: "Votre mot", type: "textarea", requis: true }}
          onValider={async (mot) => { await refuserJalon(jalon.id, mot.trim(), collaborateurId); await fermerEtRecharger(); }} />
      );
    }
    if (type === "valeur") {
      return (
        <ModaleSimple titre="Mettre à jour la valeur actuelle" libelleAction="Enregistrer" onClose={fermer}
          aide={`Cible : ${afficherNombre(jalon.cible_visee)}${jalon.cible_unite ? ` ${jalon.cible_unite}` : ""}.`}
          champ={{ libelle: "Valeur actuelle", type: "number", initial: jalon.cible_actuelle ?? "", requis: true }}
          onValider={async (v) => { await updateJalon(jalon.id, { cible_actuelle: Number(v) }, collaborateurId); await fermerEtRecharger(); }} />
      );
    }
    if (type === "supprimer") {
      return (
        <ModaleSimple titre="Supprimer le jalon" libelleAction="Supprimer" danger onClose={fermer}
          aide={`« ${jalon.intitule} » sera supprimé. Les documents qui lui sont rattachés restent sur le projet.`}
          onValider={async () => { await deleteJalon(jalon.id, collaborateurId); await fermerEtRecharger(); }} />
      );
    }
    return null;
  };

  return (
    <section className="v2-panneau v2-projet-bloc">
      <div className="v2-bloc-entete">
        <h2 className="v2-bloc-titre">
          Jalons
          {jalons && jalons.length > 0 && (
            <span className="v2-projet-compte">
              {jalons.filter((j) => j.statut === "atteint").length} / {jalons.length} atteints
            </span>
          )}
        </h2>
        {droits?.peut_gerer && (
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => setModale({ type: "creer" })}>
            Nouveau jalon
          </button>
        )}
      </div>

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {!jalons && !erreur && <div className="v2-chargement">Chargement…</div>}
      {jalons && jalons.length === 0 && (
        <p className="v2-vide">Aucun jalon. Un jalon valorise un objectif atteint ; ce n'est pas une échéance.</p>
      )}
      {jalons && jalons.length > 0 && (
        <div className="v2-jalons">
          {jalons.map((j) => (
            <CarteJalon key={j.id} jalon={j} droits={droits} onAction={onAction} />
          ))}
        </div>
      )}

      {renderModale()}
    </section>
  );
}

export default ProjetJalonsV2;
